/**
 * Base API client — Phase 6a.4 per spec §7.1.
 *
 * Native fetch with:
 *  • AbortController + 90 s default timeout (Render free-tier cold-start
 *    tolerance — wake-up takes 30–50 s).
 *  • External AbortSignal composition (caller-cancellable).
 *  • HTML response detection → ColdStartError (Render emits a static HTML
 *    "Service unavailable" page while booting, NOT JSON).
 *  • Typed error envelope: ApiError carries code, statusCode, EN+TR messages,
 *    and optional Zod field-level details.
 *
 * Components do NOT call apiFetch directly — they go through the service
 * layer (Phase 6a.5). This module is the foundation.
 */

import { supabase } from "../lib/supabaseClient";

const API_BASE = import.meta.env.VITE_API_URL;

if (!API_BASE) {
  throw new Error(
    "VITE_API_URL is not defined. Check .env.development or .env.production.",
  );
}

export interface ApiClientOptions {
  signal?: AbortSignal;
  timeout?: number;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    public statusCode: number,
    message: string,
    public messageTr?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ColdStartError extends Error {
  constructor() {
    super("Server is starting up (cold start)");
    this.name = "ColdStartError";
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  options: ApiClientOptions = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const timeoutMs = options.timeout ?? 90_000;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Combine external signal with our timeout signal so caller can cancel too.
  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
    }
  }

  try {
    // P1a — attach the Supabase access token so the backend can authenticate
    // the caller (enforced in P1b). getSession() is fast: it reads the local
    // session and refreshes in the background, returning a valid token without
    // a network round-trip. When logged out it returns null → no header is
    // sent, so public endpoints (health/wake-up) keep working. Any caller-
    // supplied Authorization in init.headers still wins (spread last).
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const authHeader: Record<string, string> = session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : {};

    const response = await fetch(url, {
      ...init,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...authHeader,
        ...init.headers,
      },
    });

    // Render's cold-start "Service unavailable" page is HTML, not JSON, and is
    // served with a 5xx gateway status while the instance spins up.
    // Finding F3 — only a non-JSON body WITH a gateway status is a cold start.
    // Previously ANY non-JSON response was thrown as ColdStartError before the
    // status was inspected, so an HTML 401/404/500 (auth page, proxy error)
    // was misclassified as a cold start, masked the real failure, and
    // triggered a spurious 5 s "server is starting up" retry.
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      if (response.status >= 502 && response.status <= 504) {
        throw new ColdStartError();
      }
      throw new ApiError(
        "NON_JSON_RESPONSE",
        response.status,
        `Server returned a non-JSON response (HTTP ${response.status})`,
      );
    }

    const json = (await response.json()) as {
      error?: {
        code?: string;
        message?: string;
        messageTr?: string;
        details?: unknown;
      };
    };

    if (!response.ok || json.error) {
      throw new ApiError(
        json.error?.code ?? "UNKNOWN_ERROR",
        response.status,
        json.error?.message ?? "Unknown error",
        json.error?.messageTr,
        json.error?.details,
      );
    }

    return json as T;
  } catch (err) {
    if (err instanceof ApiError || err instanceof ColdStartError) throw err;
    if ((err as Error).name === "AbortError") {
      throw new ApiError("TIMEOUT", 0, "Request timed out");
    }
    throw new ApiError("NETWORK_ERROR", 0, (err as Error).message);
  } finally {
    clearTimeout(timeoutId);
  }
}
